import { createSTTProvider } from './recognition/createSTTProvider';
import { fetchSTTMode } from './recognition/sttMode';
import { isIOS } from './recognition/WebSpeechSTTProvider';
import { diag, diagMediaState } from './diag';
import {
  MicCancelledError,
  MicUnavailableError,
  acquireMic,
  type MicHandle,
} from './mic';
import { claimCapture } from './session';
import type {
  STTError,
  STTMode,
  STTProvider,
  STTResult,
  WordConfidence,
} from './recognition/types';

/**
 * Warm-up window (ms) after starting recognition before the learner is told to
 * speak. iOS Safari drops audio for a few hundred ms after `start()` (the "no
 * capture on first attempt" bug), so the recorder stays `starting` until this
 * elapses, then becomes `listening`.
 */
export const READY_DELAY_MS = 500;

/**
 * iOS uses single-tap-then-auto-finish (see {@link Recorder.autoEndpoint}). If
 * the engine never delivers an end-of-speech for this long, force-stop so the
 * turn can't hang forever (e.g. the learner tapped but never spoke and
 * no-speech didn't fire).
 */
export const AUTO_STOP_SAFETY_MS = 15000;

diag('speech recorder build: 2026-10-08-raw-mic-1');
diag(
  'navigator.userAgent=',
  typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a'
);

/** A finalized transcription for one spoken turn. */
export interface MicTranscript {
  transcript: string;
  /** Engine confidence, or `null` when the engine has no trustworthy score. */
  confidence: number | null;
  /** Per-word confidence, when the provider supplies it (GCP only). */
  words?: WordConfidence[];
}

/**
 * Where the current turn is:
 * - `idle`: no turn.
 * - `preparing`: opening the microphone (iOS, and the upload paths); a tap
 *   cancels the turn.
 * - `starting`: the recognizer was started; still inside the post-start
 *   warm-up window, so the learner shouldn't speak yet.
 * - `listening`: safe to speak.
 */
export type RecorderState = 'idle' | 'preparing' | 'starting' | 'listening';

/** What to recognize on the next turn. */
export interface TurnConfig {
  /** BCP-47 language to recognize. */
  languageCode: string;
  /** Phrase hints to bias backend recognition (ignored by native engines). */
  phraseHints?: string[] | null;
  /** Active run id, for backend telemetry correlation (TR-037). */
  runId?: string | null;
  /** Step awaiting the learner, for backend telemetry correlation (TR-037). */
  stepIndex?: number | null;
}

interface RecorderEventMap {
  state: (state: RecorderState) => void;
  result: (result: MicTranscript) => void;
  error: (error: STTError) => void;
}

/** One learner turn at a time: start → (speak) → result or error → idle. */
export interface Recorder {
  readonly state: RecorderState;
  /**
   * True on iOS: recognition detects end-of-speech itself (`continuous=false`),
   * so a turn finishes without a second tap; a safety timer stops a hung turn.
   */
  readonly autoEndpoint: boolean;
  /** Begin a turn. Call from the tap handler. No-op if a turn is active. */
  start(config: TurnConfig): void;
  /**
   * Stop the current turn (learner tap). The recorder goes idle at once. While
   * `preparing` this cancels the turn outright; afterwards the final result or
   * error may still arrive.
   */
  stop(): void;
  /** Cancel a turn that is still `preparing` (e.g. input no longer expected). */
  cancelPreparing(): void;
  /** Tear down: abort any turn and release the provider. */
  dispose(): void;
  /** Subscribe; returns an unsubscribe function. */
  on<E extends keyof RecorderEventMap>(
    event: E,
    handler: RecorderEventMap[E]
  ): () => void;
}

/** Injectable dependencies (defaults wire up the real implementations). */
export interface RecorderDeps {
  createProvider?: (mode: STTMode) => STTProvider;
  fetchMode?: () => Promise<STTMode>;
  ios?: boolean;
  acquireMic?: typeof acquireMic;
}

/** Everything one turn holds; released together by `endTurn`. */
interface Turn {
  id: number;
  provider: STTProvider;
  mic: MicHandle | null;
  releaseClaim: () => void;
  /** Set when the turn ends; a late mic acquisition is released, not used. */
  over: boolean;
}

/** Map a mic-acquisition failure to the error the UI shows. */
function micError(err: unknown): STTError {
  const message = err instanceof Error ? err.message : String(err);
  if (err instanceof MicUnavailableError) {
    return { code: 'mic-unavailable', message };
  }
  const denied =
    err instanceof Error &&
    (err.name === 'NotAllowedError' || err.name === 'SecurityError');
  return { code: denied ? 'not-allowed' : 'unknown', message };
}

/**
 * Create a recorder that owns the per-turn recognition lifecycle: which
 * provider to use (from the backend `/stt/mode`, resolved ahead of the tap), a
 * fresh provider per turn, the turn's microphone stream (`speech/mic.ts`; on
 * iOS every turn, elsewhere only for providers that record audio), the
 * audio-session claim, the post-start ready delay, and the iOS safety stop.
 * Everything a turn holds is released together when it ends.
 */
export function createRecorder(deps: RecorderDeps = {}): Recorder {
  const makeProvider = deps.createProvider ?? createSTTProvider;
  const fetchMode = deps.fetchMode ?? fetchSTTMode;
  const openMic = deps.acquireMic ?? acquireMic;
  const ios = deps.ios ?? isIOS();

  let state: RecorderState = 'idle';
  let mode: STTMode = 'native';
  let disposed = false;
  let turnId = 0;
  // The turn whose provider, mic and claim are held. After a learner stop it
  // stays here (state idle) until its final result or error arrives.
  let turn: Turn | null = null;
  let readyTimer: ReturnType<typeof setTimeout> | null = null;
  let safetyTimer: ReturnType<typeof setTimeout> | null = null;

  const handlers: { [E in keyof RecorderEventMap]: Set<RecorderEventMap[E]> } =
    { state: new Set(), result: new Set(), error: new Set() };

  diag('recorder created ios=', ios);

  // Resolved ahead of the tap so the turn can start inside the gesture.
  void fetchMode().then((resolved) => {
    diag('recorder fetchSTTMode resolved mode=', resolved);
    if (!disposed) mode = resolved;
  });

  const setState = (next: RecorderState): void => {
    if (state === next) return;
    state = next;
    handlers.state.forEach((h) => h(next));
  };

  const clearTimers = (): void => {
    if (readyTimer) {
      clearTimeout(readyTimer);
      readyTimer = null;
    }
    if (safetyTimer) {
      clearTimeout(safetyTimer);
      safetyTimer = null;
    }
  };

  /**
   * Release everything the turn holds: provider first (its abort() releases
   * the recognizer's audio), then the mic stream, then the session claim.
   */
  const endTurn = (t: Turn): void => {
    if (t.over) return;
    t.over = true;
    if (turn === t) turn = null;
    diag('recorder.endTurn turn=', t.id);
    t.provider.dispose();
    t.mic?.release();
    t.mic = null;
    t.releaseClaim();
  };

  /**
   * Finalize after a result or error. On iOS this runs synchronously inside
   * the final `onresult`, while recognition is still active, so the
   * provider's abort() truly releases the audio session and the mic indicator
   * goes off.
   */
  const finishTurn = (t: Turn): void => {
    diag('recorder.finishTurn turn=', t.id);
    if (turn === t) {
      clearTimers();
      setState('idle');
    }
    endTurn(t);
  };

  const emitError = (error: STTError): void => {
    handlers.error.forEach((h) => h(error));
  };

  const handleResult = (t: Turn, result: STTResult): void => {
    if (t.over) return;
    diag(
      'recorder.handleResult isFinal=',
      result.isFinal,
      'transcript=',
      JSON.stringify(result.transcript)
    );
    // Interim results are not surfaced.
    if (!result.isFinal) return;
    const transcript: MicTranscript = {
      transcript: result.transcript,
      confidence: result.confidence,
      ...(result.words ? { words: result.words } : {}),
    };
    handlers.result.forEach((h) => h(transcript));
    finishTurn(t);
  };

  const handleError = (t: Turn, error: STTError): void => {
    if (t.over) return;
    diag('recorder.handleError code=', error.code, 'message=', error.message);
    emitError(error);
    finishTurn(t);
  };

  /** Start recognition for the turn, with its mic stream when it has one. */
  const beginRecognition = (t: Turn, config: TurnConfig): void => {
    diagMediaState(`uiTurn=${t.id} @beginRecording`);
    void t.provider.startListening({
      languageCode: config.languageCode,
      phraseHints: config.phraseHints,
      runId: config.runId,
      stepIndex: config.stepIndex,
      ...(t.mic ? { stream: t.mic.stream } : {}),
    });
    // A provider can report an error synchronously (e.g. recognition.start()
    // throwing), which has already ended the turn.
    if (t.over) return;
    clearTimers();
    setState('starting');
    readyTimer = setTimeout(() => {
      readyTimer = null;
      diag('recorder ready delay elapsed turn=', t.id);
      if (turn === t && state === 'starting') setState('listening');
    }, READY_DELAY_MS);
    if (ios) {
      safetyTimer = setTimeout(() => {
        safetyTimer = null;
        diag('safety timer fired turn=', t.id);
        if (turn === t && state !== 'idle') t.provider.stopListening();
      }, AUTO_STOP_SAFETY_MS);
    }
  };

  /** Cancel a turn that hasn't started recognition yet. */
  const cancelPreparing = (): void => {
    if (!turn || state !== 'preparing') return;
    diag('recorder: cancel while preparing turn=', turn.id);
    const t = turn;
    clearTimers();
    setState('idle');
    endTurn(t);
  };

  return {
    get state(): RecorderState {
      return state;
    },
    autoEndpoint: ios,

    start(config: TurnConfig): void {
      if (disposed || state !== 'idle') return;
      // A stopped turn still waiting for its result is dropped: a new turn
      // gets its own provider and mic.
      if (turn) endTurn(turn);
      const id = ++turnId;
      diag('recorder.start turn=', id, 'mode=', mode);
      // WebKit only allows capture under 'play-and-record': claim
      // synchronously, inside the tap, before opening the mic.
      const releaseClaim = claimCapture(`recorder turn ${id}`);
      let provider: STTProvider;
      try {
        provider = makeProvider(mode);
      } catch (error) {
        diag('recorder.start THREW', error);
        releaseClaim();
        return;
      }
      const t: Turn = { id, provider, mic: null, releaseClaim, over: false };
      turn = t;
      provider.addEventListener('result', (r) => handleResult(t, r));
      provider.addEventListener('error', (e) => handleError(t, e));

      if (!ios && !provider.capturesAudio) {
        // Desktop Web Speech: the engine opens the mic itself.
        beginRecognition(t, config);
        return;
      }
      setState('preparing');
      openMic({ ios, isCancelled: () => t.over })
        .then((mic) => {
          if (t.over) {
            mic.release();
            return;
          }
          t.mic = mic;
          beginRecognition(t, config);
        })
        .catch((err: unknown) => {
          if (t.over || err instanceof MicCancelledError) return;
          diag('recorder: mic acquisition failed', err);
          emitError(micError(err));
          finishTurn(t);
        });
    },

    stop(): void {
      diag('recorder.stop (user tap) state=', state);
      if (state === 'preparing') {
        cancelPreparing();
        return;
      }
      turn?.provider.stopListening();
      clearTimers();
      setState('idle');
    },

    cancelPreparing,

    dispose(): void {
      diag('recorder.dispose');
      disposed = true;
      clearTimers();
      if (turn) endTurn(turn);
      setState('idle');
      handlers.state.clear();
      handlers.result.clear();
      handlers.error.clear();
    },

    on(event, handler) {
      const set = handlers[event] as Set<typeof handler>;
      set.add(handler);
      return (): void => {
        set.delete(handler);
      };
    },
  };
}
